import Head from "next/head";
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";
import InputDateComponent from "@/components/InputComponent/InputDateComponent";
import InputFileComponent from "@/components/InputComponent/InputFileComponent";
import InputNumberComponent from "@/components/InputComponent/InputNumberComponent";
import InputTextAreaComponent from "@/components/InputComponent/InputTextAreaComponent";
import InputTextComponent from "@/components/InputComponent/InputTextComponent";
import MultiSelectFieldset from "@/components/InputComponent/MultiSelectComponent";
import SelectFieldset from "@/components/InputComponent/SelectComponent";
import { useRouter } from "next/router";
import { useState } from "react";
import toast from "react-hot-toast";

const data = {
  fullName: {
    type: "text",
    placeholder: "Enter First Name",
    defaultValue: "Enter First Name",
    required: true,
    label: "Full Name",
    isOTPRequired: false,
    priority: 1,
    value: "",
    inputKey: "fullName",
  },
  phone: {
    type: "number",
    placeholder: "Enter Phone Number without +91",
    defaultValue: "",
    required: true,
    label: "Phone",
    isOTPRequired: true,
    priority: 3,
    error: null,
    value: "",
    inputKey: "phone",
  },
  email: {
    type: "text",
    placeholder: "Enter Email",
    defaultValue: "",
    required: false,
    label: "Email",
    isOTPRequired: true,
    priority: 4,
    error: null,
    value: "",
    inputKey: "email",
  },
  enquiry: {
    type: "textarea",
    placeholder: "Enter your enquiry",
    defaultValue: "",
    required: false,
    label: "Enquiry",
    isOTPRequired: true,
    priority: 5,
    error: null,
    value: "",
    inputKey: "enquiry",
  },
};

const ContactUs = () => {
  const [validForm, setValidForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    enquiry: "",
  });
  const router = useRouter();

  const onInputChange = (e) => {
    const { name, value } = e.target;
    if (!Object.keys(formData).includes(name)) {
      return;
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    checkIfFormIsValid();
  };

  const checkIfFormIsValid = () => {
    const requiredFields = Object.keys(formData).filter(
      (key) => data[key].required
    );
    for (let key of requiredFields) {
      if (formData[key] === "") {
        setValidForm(false);
        return;
      }
    }
    setValidForm(true);
  };

  const handleOnSubmit = () => {
    if (!validForm) {
      toast.error("Please fill all the required fields");
      return;
    }
  };

  return (
    <>
      <Head>
        <title>Contact BookMyPooja | Book a Pandit by Phone or WhatsApp</title>
        <meta name="description" content="Contact BookMyPooja to book a pandit or ask about a puja. Call or WhatsApp +91 98699 29292, or send us your details and our team will get back to you." />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_SITE_URL || "https://bookmypoojaonline.com"}/contact-us`} />
      </Head>
      <section className="bg-white py-8 px-4 xs:px-6 sm:px-8 lg:py-16 md:px-12 lg:px-16 xl:px-20 xxl:px-24 3xl:px-35">
        <div className="flex flex-col gap-8 md:flex-row lg:justify-center lg:gap-12 my-auto">
          <div className="w-full lg:w-1/2 h-auto">
            <div className="px-6 py-8 bg-white shadow-lg rounded-lg lg:max-w-lg mx-auto">
              <h1 className="text-xl font-semibold text-gray-800 lg:text-2xl mb-6">
                Submit Your Enquiry Here
              </h1>

              <div className="overflow-y-scroll max-h-[60vh] scrollbar-hide rounded-lg shadow-lg">
                <section className="flex flex-col gap-3 p-4">
                  {Object.keys(data).map((key) => {
                    const componentProps = {
                      data: data[key],
                      value: formData[key].value,
                      onInputChange: onInputChange,
                    };
                    switch (data[key].type) {
                      case "select":
                        return <SelectFieldset key={key} {...componentProps} />;
                      case "multi-select":
                        return (
                          <MultiSelectFieldset key={key} {...componentProps} />
                        );
                      case "text":
                        return (
                          <InputTextComponent key={key} {...componentProps} />
                        );
                      case "number":
                        return (
                          <InputNumberComponent key={key} {...componentProps} />
                        );
                      case "date":
                        return (
                          <InputDateComponent key={key} {...componentProps} />
                        );
                      case "file":
                        return (
                          <InputFileComponent key={key} {...componentProps} />
                        );
                      case "textarea":
                        return (
                          <InputTextAreaComponent key={key} {...componentProps} />
                        );
                      default:
                        return null;
                    }
                  })}
                  <button
                    className={`${
                      !validForm
                        ? "bg-gray-400 text-gray-50"
                        : `bg-BluntRed text-white`
                    } h-10 w-full rounded-lg`}
                    onClick={handleOnSubmit}
                  >
                    Submit Enquiry
                  </button>
                </section>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2 m-auto">
            <h1 className="text-2xl font-semibold text-BluntRed capitalize lg:text-3xl mb-6">
              Contact us for more info
            </h1>

            <div className="space-y-6">
              <p className="flex items-start space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-BluntRed"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-gray-700">
                  Krishnappa Garden, C V Raman Nagar, Bengaluru Karnataka 560093
                </span>
              </p>

              <p className="flex items-start space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-BluntRed"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="text-gray-700">(+91) 9869929292</span>
              </p>

              <p className="flex items-start space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 text-BluntRed"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-gray-700">support@bookmypooja.online</span>
              </p>

              <div className="mt-6">
                <h3 className="text-gray-600 mb-3">Follow us</h3>
                <div className="flex gap-4">
                  <ImageWrapper
                    src="/icons/fb-icon.png"
                    alt="Facebook"
                    width={30}
                    height={30}
                    onClick={() =>
                      router.push(
                        "https://www.facebook.com/p/Bookmypooja-100081370088277/"
                      )
                    }
                  />
                  <ImageWrapper
                    src="/icons/instagram.svg"
                    alt="Instagram"
                    width={30}
                    height={30}
                    onClick={() =>
                      router.push("https://www.instagram.com/bookmypooja.online/")
                    }
                  />
                  <ImageWrapper
                    src="/icons/linkedin.svg"
                    alt="LinkedIn"
                    width={30}
                    height={30}
                    onClick={() =>
                      router.push(
                        "https://www.linkedin.com/company/bookmypooja1/"
                      )
                    }
                  />
                  <ImageWrapper
                    src="/icons/x-icon.png"
                    alt="X"
                    width={30}
                    height={30}
                    onClick={() =>
                      router.push(
                        "https://x.com/bookmypooja1?t=NIv60KKoU47ytqTN996lmQ&s=08"
                      )
                    }
                  />
                  <ImageWrapper
                    src="/icons/youtube.svg"
                    alt="YouTube"
                    width={30}
                    height={30}
                    onClick={() =>
                      router.push("https://www.youtube.com/@bookmypoojaonline242")
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUs;
